import { useEffect, useState, ElementType } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import './ShareableHeading.css';

interface Props {
  id: string;
  as?: ElementType;
  children: React.ReactNode;
  className?: string;
}

/**
 * A heading that shows a # anchor button on hover.
 * Clicking it copies the section URL (path + #id) to the clipboard.
 * Usage: <ShareableHeading id="batches" as="h2" className="dv-section-title">...</ShareableHeading>
 */
export default function ShareableHeading({ id, as: Tag = 'h2', children, className }: Props) {
  const [status, setStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const { t } = useGlossary();

  useEffect(() => {
    const rawHash = window.location.hash.slice(1);
    if (!rawHash) return;

    let normalizedHash = rawHash;
    try {
      normalizedHash = decodeURIComponent(rawHash);
    } catch {
      normalizedHash = rawHash;
    }

    if (normalizedHash !== id) return;

    const element = document.getElementById(id);
    if (!element) return;

    window.requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [id]);

  function copyLink() {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url).then(
      () => {
        setStatus('copied');
        setTimeout(() => setStatus('idle'), 2200);
      },
      () => setStatus('error')
    );
  }

  return (
    <Tag id={id} className={`shareable-heading ${className ?? ''}`}>
      {children}
      <button
        className={`sh-anchor-btn ${status}`}
        onClick={(e) => { e.stopPropagation(); copyLink(); }}
        title={t('shareableHeading.title')}
        aria-label={t('shareableHeading.aria', { id })}
      >
        {status === 'copied' ? '✓' : status === 'error' ? '✕' : '#'}
      </button>
      {status === 'copied' && (
        <span className="sh-toast" role="status">{t('shareableHeading.toast')}</span>
      )}
    </Tag>
  );
}
